import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Checkbox,
  Typography,
  Box,
  FormControlLabel,
  Container,
  Paper,
  Link,
  CircularProgress,
} from "@mui/material";
import { Check } from "@mui/icons-material";
import { NavLink } from "react-router-dom";
import api from "../../services/api";
import { isValidPhone, formatPhoneNumber } from "../../utils/validation";

const ContactUsPage = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    message: "",
    agree: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    document.title = "Contact Us";
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: formatPhoneNumber(value) });
  };

  const handleCheckboxChange = (e) => {
    setFormData({ ...formData, agree: e.target.checked });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = "First name is required";
    if (!formData.lastName) newErrors.lastName = "Last name is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Invalid email address";
    if (!isValidPhone(formData.phoneNumber))
      newErrors.phoneNumber = "Phone number must be 10 digits";
    if (!formData.message) newErrors.message = "Message is required";
    if (!formData.agree)
      newErrors.agree = "You must agree to the privacy policy";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      setErrors({});
      setIsSubmitting(true);

      try {
        await api.post("/contact-messages", {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          message: formData.message,
          agreedToPolicy: formData.agree,
        });

        setIsSubmitting(false);
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            firstName: "",
            lastName: "",
            email: "",
            phoneNumber: "",
            message: "",
            agree: false,
          });
        }, 5000);
      } catch (error) {
        console.error("Failed to send message:", error);
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Box sx={{ py: 10, backgroundColor: "#FFF2EF" }}>
      <Container maxWidth="md">
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{
            fontFamily: "Moul",
            fontSize: { xs: "1.8rem", sm: "2rem" }, // slightly smaller heading on xs
            lineHeight: 1.2,
          }}>
          We’d Love to Hear From You
        </Typography>

        <Box
          sx={{
            width: 60,
            height: 4,
            backgroundColor: "#FF6F61",
            borderRadius: 2,
            mx: "auto",
            mb: 2,
          }}
        />

        <Typography
          variant="body1"
          textAlign="center"
          sx={{
            fontFamily: "Inter",
            maxWidth: { xs: "100%", sm: 600 },
            mx: "auto",
            mb: 1,
            fontSize: { xs: "0.95rem", sm: "1rem" },
            lineHeight: 1.6,
          }}>
          Drop us a message and we’ll get back to you within 1–2 business days.
        </Typography>

        <Typography
          variant="body1"
          textAlign="center"
          sx={{
            fontFamily: "Inter",
            maxWidth: { xs: "100%", sm: 600 },
            mx: "auto",
            fontSize: { xs: "0.95rem", sm: "1rem" },
            lineHeight: 1.6,
            mb: 3,
          }}>
          Or who knows — if you’re lucky, much sooner! 😊
        </Typography>

        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            maxWidth: 600,
            mx: "auto",
            boxShadow: 3,
            backgroundColor: "#fff",
          }}>
          <form onSubmit={handleSubmit}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
                mb: 2,
              }}>
              <TextField
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                error={!!errors.firstName}
                helperText={errors.firstName}
                fullWidth
                sx={{ fontFamily: "Inter" }}
              />
              <TextField
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
                fullWidth
                sx={{ fontFamily: "Inter" }}
              />
            </Box>

            <TextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              sx={{ mb: 2, fontFamily: "Inter" }}
            />

            <TextField
              label="Phone Number"
              name="phoneNumber"
              placeholder="(123) 456-7890"
              value={formData.phoneNumber}
              onChange={handlePhoneChange}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
              fullWidth
              sx={{ mb: 2, fontFamily: "Inter" }}
            />

            <TextField
              label="Message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              error={!!errors.message}
              helperText={errors.message}
              fullWidth
              multiline
              rows={4}
              sx={{ mb: 2, fontFamily: "Inter" }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.agree}
                  onChange={handleCheckboxChange}
                  sx={{
                    color: formData.agree ? "#FD7762" : "grey",
                    "&.Mui-checked": { color: "#FD7762" },
                  }}
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{" "}
                  <Link
                    component={NavLink}
                    to="/privacy-policy"
                    underline="hover">
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link
                    component={NavLink}
                    to="/terms-and-conditions"
                    underline="hover">
                    Terms of Use
                  </Link>
                </Typography>
              }
            />
            {errors.agree && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {errors.agree}
              </Typography>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting}
              startIcon={isSubmitted ? <Check /> : null}
              sx={{
                mt: 4,
                py: 1.5,
                backgroundColor: isSubmitted ? "green" : "#FD7762",
                "&:hover": { backgroundColor: "#FD7762CC" },
                fontWeight: 500,
                fontSize: "1rem",
              }}>
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : isSubmitted ? (
                "Message Received"
              ) : (
                "Send Message"
              )}
            </Button>

            {isSubmitted && (
              <Typography
                variant="body2"
                align="center"
                color="success.main"
                sx={{ mt: 2 }}>
                We’ll get back to you soon!
              </Typography>
            )}
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default ContactUsPage;
